import { app, updater } from "@neutralinojs/lib";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { useToast } from "@/components/hooks/use-toast";
import { Button } from "@/components/ui/button";

declare const __APP_VERSION__: string;

const SIMULATE_OUT_OF_DATE = false;

const APP_DOWNLOAD_HOST = import.meta.env.VITE_APP_DOWNLOAD_HOST;

// Type guard to check if Neutralino is available
function isNeutralinoAvailable(): boolean {
  return typeof window !== "undefined" && !!window.NL_APPVERSION;
}

function compareVersions(v1: string, v2: string): number {
  if (SIMULATE_OUT_OF_DATE) {
    return 1;
  }
  const parts1 = v1.split(".").map(Number);
  const parts2 = v2.split(".").map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }
  return 0;
}

const ManifestSchema = z.object({
  version: z.string(),
});

export type Manifest = z.infer<typeof ManifestSchema>;

const NeutralinoUpdater: React.FC = () => {
  const { toast } = useToast();
  const [lastDismissedTime, setLastDismissedTime] = useState<number | null>(
    null,
  );

  const handleUpdate = useCallback(async () => {
    if (isNeutralinoAvailable()) {
      try {
        await updater.install();
        app.restartProcess();
      } catch (error) {
        console.error("Error updating application:", error);
      }
    } else {
      console.log("Update not available in browser environment");
    }
  }, []);

  const showUpdateToast = useCallback(() => {
    toast({
      title: "An app update is available",
      description: "Update to get the latest features",
      action: (
        <Button variant="outline" onClick={handleUpdate}>
          Update
        </Button>
      ),
      duration: Number.POSITIVE_INFINITY,
      onOpenChange: (open) => {
        if (!open) {
          setLastDismissedTime(Date.now());
        }
      },
    });
  }, [toast, handleUpdate]);

  const checkForUpdates = useCallback(async () => {
    try {
      let manifest: { version: string } | undefined;
      if (isNeutralinoAvailable()) {
        manifest = await updater.checkForUpdates(
          `https://${APP_DOWNLOAD_HOST}/app/v1/updates/manifest.json`,
        );
      } else {
        const response = await fetch(
          `https://${APP_DOWNLOAD_HOST}/app/v1/updates/manifest.json`,
        );
        const rawManifest = await response.json();
        manifest = ManifestSchema.parse(rawManifest);
      }
      const currentVersion = __APP_VERSION__;
      const latestVersion = manifest.version;

      if (compareVersions(latestVersion, currentVersion) > 0) {
        const now = Date.now();
        if (
          !lastDismissedTime ||
          now - lastDismissedTime > 2 * 60 * 60 * 1000
        ) {
          showUpdateToast();
        }
      }
    } catch (error) {
      console.error("Error checking for updates:", error);
    }
  }, [lastDismissedTime, showUpdateToast]);

  useEffect(() => {
    checkForUpdates();
    const intervalId = setInterval(checkForUpdates, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [checkForUpdates]);

  // This component doesn't render anything directly
  return null;
};

export default NeutralinoUpdater;
